import React from 'react'
import {
    Button, Icon, IconButton, Menu, MenuItem, Paper, Snackbar, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TableSortLabel
} from '@material-ui/core'
import { ConfirmDialog, MatrixForm, Search, FormDialog, FixedLoadingIndicator, ServerErrorHandler, LabelDisplayedRows } from 'components'
import { useInputValue, useTableModel } from 'hooks'
import {
    NEW_PATH, RESOURCES_PATH, EDIT_PATH, SMALL_ROWS_PER_PAGINATION_OPTIONS, MATRIX_PATH, deleteMatrixService,
    MATRIX_TABLE_COLUMNS, ROOT_TITLE, MATRIX_TITLE
} from 'lib'
import { useHistory, useLocation } from 'react-router'
import { IMessageConfig, IMatrix } from 'types'
import { ManagementContext } from 'context'
import { Alert } from '@material-ui/lab'
import { Helmet } from 'react-helmet'

const Matrixes = () => {

    const { matrixes, accountTypes, offices, refreshMatrixes, refreshAccountTypes, refreshOffices } = React.useContext(ManagementContext)
    const { value: query, clearValue: handleClear, onChange: handleChange } = useInputValue()
    const { createSortHandler, handleChangePage, handleChangeRowsPerPage, order, orderBy, page, rows, rowsPerPage } = useTableModel()
    const location = useLocation()
    const history = useHistory()
    const [selected, setSelected] = React.useState<any | undefined>(undefined)
    const [actionsAnchorEl, setActionsAnchorEl] = React.useState<null | HTMLElement>(null)
    const [deleteId, setDeleteId] = React.useState<number>(-1)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<boolean>(false)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: "", severity: "info" })
    const getModel = React.useCallback((): any[] => {
        const src = [...matrixes]
        if (Boolean(query)) {
            return src.filter((s: any) => {
                return (
                    s.matrixCode.toUpperCase().includes(query.toUpperCase()) ||
                    s.description.toUpperCase().includes(query.toUpperCase())
                )
            })
        }
        return src
    }, [matrixes, query])
    const handleEdit = () => {
        setActionsAnchorEl(null)
        history.push({
            state: {
                edit: true,
                values: selected
            },
            pathname: `${RESOURCES_PATH}${MATRIX_PATH}${EDIT_PATH}`
        })
    }
    const handleConfirmDelete = (id: number) => {
        setActionsAnchorEl(null)
        setDeleteId(id)
    }
    const handleDelete = async () => {
        try {
            setLoading(true)
            await deleteMatrixService(deleteId)
            setMessageConfig({ open: true, message: "Matriz eliminada!", severity: "success" })
            await refreshMatrixes()
            setLoading(false)
            return true
        } catch (error) {
            setMessageConfig({ open: true, message: "No se pudo eliminar la matriz...!", severity: "error" })
            setLoading(false)
            return false
        }
    }
    React.useEffect(() => {
        const initScreen = async () => {
            try {
                setLoading(true)
                await refreshMatrixes()
                await refreshOffices()
                await refreshAccountTypes()
                setLoading(false)
            } catch (error) {
                setLoading(false)
                setError(true)
            }
        }
        initScreen()
    }, [refreshMatrixes, refreshOffices, refreshAccountTypes])
    return (
        <Paper className="flex flex-col h-full overflow-hidden p-4 pb-0">
            <Helmet>
                <title>{`${ROOT_TITLE} - ${MATRIX_TITLE}`}</title>
            </Helmet>
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <Search
                        onChange={handleChange}
                        query={query}
                        onClear={handleClear}
                        placeholer="Buscar por código o descripción..."
                    />
                </div>

                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={() => history.push(`${RESOURCES_PATH}${MATRIX_PATH}${NEW_PATH}`)}
                >
                    <Icon fontSize="small" style={{ marginRight: 5 }}>add</Icon>
                    {"Nuevo"}
                </Button>
                <FormDialog
                    open={Boolean(location.pathname === `${RESOURCES_PATH}${MATRIX_PATH}${NEW_PATH}` || location.pathname === `${RESOURCES_PATH}${MATRIX_PATH}${EDIT_PATH}`)}
                    onClose={() => history.push(`${RESOURCES_PATH}${MATRIX_PATH}`)}
                >
                    <MatrixForm
                        onSuccess={() => { setMessageConfig({ open: true, message: Boolean(selected) ? "Matriz actualizada!" : " registrada!", severity: "success" }); refreshMatrixes(); if (Boolean(selected)) { setSelected(undefined) } }}
                        onError={() => setMessageConfig({ open: true, message: "No se pude registrar la matriz", severity: "error" })}
                        onClose={() => history.push(`${RESOURCES_PATH}${MATRIX_PATH}`)}
                    />
                </FormDialog>
            </div>
            <div className="flex h-full flex-col mt-4 overflow-hidden">
                <div className="flex flex-grow overflow-auto">
                    <TableContainer>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {MATRIX_TABLE_COLUMNS.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            align={'left'}
                                            padding={"none"}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={createSortHandler(headCell.id as any)}
                                            >
                                                {headCell.label}
                                                {orderBy === headCell.id ? (
                                                    <span className="hidden">
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        padding={"default"}
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(rows(getModel()) as IMatrix[]).map((row, index) => {
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={index.toString()}
                                        >
                                            <TableCell component="th" scope="row" padding="none">{row.matrixCode}</TableCell>
                                            <TableCell padding="none" align="left">{row.description}</TableCell>
                                            <TableCell padding="none" align="left">{accountTypes.find(accountTypeT => accountTypeT.accountTypeId === row.accountTypeId)?.description}</TableCell>
                                            <TableCell padding="none" align="left">{offices.find(officeT => officeT.officeId === row.officeId)?.description}</TableCell>
                                            <TableCell padding="none" align="left">
                                                <span
                                                    className={
                                                        row.active
                                                            ? "active-state-label"
                                                            : "inactive-state-label"
                                                    }
                                                >
                                                    {row.active ? "Activa" : "Inactiva"}
                                                </span>
                                            </TableCell>
                                            <TableCell align="left">
                                                <IconButton onClick={(e) => { setActionsAnchorEl(e.currentTarget); setSelected(row) }} size="small">
                                                    <Icon fontSize="small">more_vert</Icon>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                <Menu
                                    anchorEl={actionsAnchorEl}
                                    open={Boolean(actionsAnchorEl)}
                                    onClose={() => setActionsAnchorEl(null)}
                                >
                                    <MenuItem onClick={handleEdit} dense>{"Editar"}</MenuItem>
                                    <MenuItem onClick={() => handleConfirmDelete(selected?.matrixId ?? -1)} dense>{"Eliminar"}</MenuItem>
                                </Menu>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                <div>
                    <TablePagination
                        rowsPerPageOptions={SMALL_ROWS_PER_PAGINATION_OPTIONS}
                        component="div"
                        count={getModel().length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Matrices por página"
                        labelDisplayedRows={LabelDisplayedRows}
                    />
                </div>
            </div>
            <ConfirmDialog
                onCancel={() => setDeleteId(-1)}
                onConfirm={handleDelete}
                open={Boolean(deleteId !== -1)}
                title={"¿Estás seguro que deseas eliminar?"}
                loading={loading}
            />
            <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig({ ...messageConfig, open: false })} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                <Alert variant="filled" onClose={() => setMessageConfig({ ...messageConfig, open: false })} severity={messageConfig.severity}>
                    {messageConfig.message}
                </Alert>
            </Snackbar>
            <FixedLoadingIndicator loading={loading && Boolean(deleteId === -1)} />
            <ServerErrorHandler
                error={error}
                onSuccess={() => setError(false)}
                tryAgain={refreshMatrixes}
            />
        </Paper>
    )
}


export default Matrixes
